import { memo, ReactNode, useCallback, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProps,
} from "react-beautiful-dnd";

export const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

type DragDropListProp = {
  items: any[];
  children: (item: any, index: number, dragHandleProps?: any) => ReactNode;
  handleChange: (newItems: any[]) => void;
  isHorizontal?: boolean;
};

const DragDropList = memo(function DragDropListFn(props: DragDropListProp) {
  const { items, children, handleChange, isHorizontal } = props;

  const handleOnDrag = useCallback(
    (result: any) => {
      if (!result.destination) return;

      const reorderedItem = items[parseInt(result.source.index)];
      const newItems = [...items];
      newItems.splice(result.source.index, 1);
      newItems.splice(result.destination.index, 0, reorderedItem);

      handleChange(newItems);
    },
    [items, handleChange]
  );

  return (
    <DragDropContext onDragEnd={(result) => handleOnDrag(result)}>
      <StrictModeDroppable
        droppableId="characters"
        direction={isHorizontal ? "horizontal" : "vertical"}
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`characters ${
              isHorizontal ? "AlignedRow" : "ColumnNormal"
            }`}
            style={{ gap: 10 }}
          >
            {items.map((item, index) => {
              return (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided) => {
                    const { style: dragStyle, ...dragRest } =
                      provided.draggableProps;
                    return (
                      <div
                        ref={provided.innerRef}
                        {...dragRest}
                        style={{
                          ...dragStyle,
                          ...(isHorizontal ? { flexShrink: 0 } : undefined),
                        }}
                      >
                        {children(item, index, provided.dragHandleProps)}
                      </div>
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
});

export default DragDropList;
